import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { Check, Star, ShoppingCart } from 'lucide-react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Card } from '../components/ui/card.js'
import { AddOnCard } from '../components/AddOnCard.js'
import { useDemoStore, type DemoState } from '../state/store.js'
import { computeAddOns } from '../lib/addons.js'
import { activePersona } from '../lib/persona.js'

/**
 * Fires a single celebratory confetti burst, honoring prefers-reduced-motion.
 * Dynamically imports canvas-confetti so the ~5KB lib only loads when the
 * user actually completes a demo run.
 */
const fireConfetti = async (): Promise<void> => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  const { default: confetti } = await import('canvas-confetti')
  // Desktop preview centers the phone in the viewport, so the burst origin
  // is the same as on mobile.
  confetti({
    particleCount: 140,
    spread: 75,
    origin: { x: 0.5, y: 0.55 },
    colors: ['#2A52D6', '#7E9BEA', '#0B1F3A', '#F4F2EC'],
    disableForReducedMotion: true,
  })
}

export const ConfirmationScreen = (): ReactElement => {
  const { state, dispatch } = useDemoStore()
  const persona = activePersona()
  /**
   * Snapshot of the demo state captured at the moment "Complete Purchase"
   * is clicked. The reducer is RESET immediately after so the next demo
   * run starts clean (no accumulated saved photos, no carried-over
   * add-ons). The completion view renders from this snapshot so the user
   * still sees the technician and the add-ons they actually picked.
   */
  const [snapshot, setSnapshot] = useState<DemoState | null>(null)
  const completed = snapshot !== null

  const { all } = useMemo(() => computeAddOns(state), [state])
  const recommended = all.filter(a => a.recommended)
  const other = all.filter(a => !a.recommended)

  const handleComplete = (): void => {
    setSnapshot(state)
    dispatch({ type: 'RESET' })
  }

  useEffect(() => {
    if (!completed) return
    void fireConfetti()
  }, [completed])

  if (snapshot) {
    const snapshotAll = computeAddOns(snapshot).all
    return (
      <AppShell title="Confirmation & add-ons" progress={1}>
        <div className="text-center">
          <div className="mx-auto w-20 h-20 rounded-full border-2 border-primary text-primary flex items-center justify-center">
            <Check className="h-8 w-8" />
          </div>
          <div className="text-xs font-semibold tracking-wider text-primary uppercase mt-3">Booking confirmed</div>
          <h1 className="mt-2">You're all set!</h1>
        </div>
        <Card className="mt-6 p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">{persona.technician.initials}</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold tracking-wider text-primary uppercase">Assigned technician</div>
            <div className="font-semibold inline-flex items-center gap-1">
              {persona.technician.name}
              <span className="inline-flex items-center text-primary ml-1">
                <Star className="h-3.5 w-3.5 fill-current" /> {persona.technician.rating}
              </span>
            </div>
          </div>
        </Card>
        <div className="text-xs font-semibold tracking-wider text-primary uppercase mt-6 mb-2 inline-flex items-center gap-1.5">
          <ShoppingCart className="h-3.5 w-3.5" />
          Your add-ons
        </div>
        {snapshot.addOns.length === 0
          ? <Card className="p-4 text-muted-foreground text-center text-sm">No optional add-ons were added to this order.</Card>
          : <ul className="space-y-1 text-sm">{snapshot.addOns.map(id => <li key={id}>{snapshotAll.find(a => a.id === id)?.title}</li>)}</ul>}
        <p className="text-xs text-muted-foreground text-center mt-10">
          Purchase complete — you can close this window anytime.
        </p>
      </AppShell>
    )
  }

  return (
    <AppShell
      title="Confirmation & add-ons"
      showBack onBack={() => history.back()}
      progress={1}
      footer={<Button size="xl" onClick={handleComplete}>Complete Purchase</Button>}
    >
      <div className="text-center mb-5">
        <div className="mx-auto w-16 h-16 rounded-full border-2 border-primary text-primary flex items-center justify-center">
          <Check className="h-6 w-6" />
        </div>
        <div className="text-xs font-semibold tracking-wider text-primary uppercase mt-2">Booking confirmed</div>
        <h1 className="mt-1">You're all set!</h1>
      </div>

      <Card className="mb-5 p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">{persona.technician.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold tracking-wider text-primary uppercase">Assigned technician</div>
          <div className="font-semibold inline-flex items-center gap-1">
            {persona.technician.name}
            <span className="inline-flex items-center text-primary ml-1">
              <Star className="h-3.5 w-3.5 fill-current" /> {persona.technician.rating}
            </span>
          </div>
        </div>
      </Card>

      <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-1 inline-flex items-center gap-1.5">
        <ShoppingCart className="h-3.5 w-3.5" />
        Recommended add-ons
      </div>
      {state.countertop && (
        <p className="text-sm text-muted-foreground mb-3">
          Your assessment noted a {state.countertop.material.toLowerCase()} countertop — we highlighted the mounting kit installers often need.
        </p>
      )}
      {recommended.map(a => (
        <AddOnCard key={a.id} addon={a} selected={state.addOns.includes(a.id)} onToggle={() => dispatch({ type: 'TOGGLE_ADDON', id: a.id })} />
      ))}

      {other.length > 0 && (
        <>
          <div className="text-xs font-semibold tracking-wider text-primary uppercase mt-2 mb-1">Other options</div>
          {other.map(a => (
            <AddOnCard key={a.id} addon={a} selected={state.addOns.includes(a.id)} onToggle={() => dispatch({ type: 'TOGGLE_ADDON', id: a.id })} />
          ))}
        </>
      )}
    </AppShell>
  )
}
